import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { BarChart3, ShoppingBag, Package, Tag, Ticket, FileText, Plus, Trash2, Truck, X } from 'lucide-react';
import { toast } from 'sonner';
import { AdminStats } from '../components/admin/AdminStats';
import { AdminProducts } from '../components/admin/AdminProducts';
import { AdminPromoCodes } from '../components/admin/AdminPromoCodes';
import { AdminPages } from '../components/admin/AdminPages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ id: '', name: '', description: '' });

  const [shippingOrder, setShippingOrder] = useState(null);
  const [shippingForm, setShippingForm] = useState({ carrier: 'Colissimo', tracking_number: '' });
  const [shippingLoading, setShippingLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'stats') fetchStats();
    if (activeTab === 'orders') fetchOrders();
    if (activeTab === 'products') { fetchProducts(); fetchCategories(); }
    if (activeTab === 'categories') { fetchCategories(); fetchProducts(); }
  }, [activeTab]);

  const fetchStats = async () => {
    try { const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true }); setStats(data); }
    catch (e) { console.error(e); }
  };
  const fetchOrders = async () => {
    try { const { data } = await axios.get(`${API}/admin/orders`, { withCredentials: true }); setOrders(data); }
    catch (e) { console.error(e); }
  };
  const fetchProducts = async () => {
    try { const { data } = await axios.get(`${API}/products`); setProducts(data); }
    catch (e) { console.error(e); }
  };
  const fetchCategories = async () => {
    try { const { data } = await axios.get(`${API}/categories`); setCategories(data); }
    catch (e) { console.error(e); }
  };

  // ===== Categories =====
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!/^[a-z0-9\-]+$/.test(newCategory.id)) {
      toast.error("L'identifiant doit être en minuscules (a-z, 0-9, -)");
      return;
    }
    try {
      await axios.post(`${API}/admin/categories`, newCategory, { withCredentials: true });
      toast.success('Catégorie créée');
      setShowAddCategory(false);
      setNewCategory({ id: '', name: '', description: '' });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ? (impossible si des produits l\'utilisent)')) return;
    try {
      await axios.delete(`${API}/admin/categories/${id}`, { withCredentials: true });
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // ===== Orders =====
  const CARRIERS = ['Colissimo', 'Chronopost', 'DPD', 'Mondial Relay', 'GLS', 'UPS', 'DHL', 'Autre'];

  const updateOrderStatus = async (orderId, status) => {
    if (status === 'shipped') {
      setShippingOrder(orders.find((o) => o.id === orderId));
      setShippingForm({ carrier: 'Colissimo', tracking_number: '' });
      return;
    }
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status }, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const submitShipping = async (e) => {
    e.preventDefault();
    if (!shippingForm.tracking_number.trim()) { toast.error('Numéro de suivi requis'); return; }
    setShippingLoading(true);
    try {
      await axios.put(`${API}/admin/orders/${shippingOrder.id}/status`,
        { status: 'shipped', ...shippingForm }, { withCredentials: true });
      toast.success('Commande expédiée, email de suivi envoyé au client');
      setShippingOrder(null);
      fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setShippingLoading(false); }
  };

  if (!user || user.role !== 'admin') {
    return <div className="min-h-screen flex items-center justify-center"><p className="text-destructive">Accès non autorisé</p></div>;
  }

  const TABS = [
    { id: 'stats', label: 'Statistiques', icon: BarChart3 },
    { id: 'orders', label: 'Commandes', icon: ShoppingBag },
    { id: 'products', label: 'Produits & stock', icon: Package },
    { id: 'categories', label: 'Catégories', icon: Tag },
    { id: 'promos', label: 'Codes promo', icon: Ticket },
    { id: 'pages', label: 'Pages légales', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-8" style={{ fontFamily: 'IBM Plex Sans' }}>
          Tableau de bord Admin
        </h1>

        <div className="bg-white rounded-sm border border-border p-2 mb-8 flex gap-1 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm font-medium whitespace-nowrap ${
                  activeTab === tab.id ? 'bg-brand-orange text-white' : 'hover:bg-surface'
                }`}
                data-testid={`admin-${tab.id}-tab`}>
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'stats' && <AdminStats stats={stats} />}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-sm border border-border p-6">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'IBM Plex Sans' }}>Gestion des commandes</h2>
            <div className="space-y-4">
              {orders.length === 0 && <p className="text-muted-foreground">Aucune commande pour le moment</p>}
              {orders.map((order) => (
                <div key={order.id} className="border border-border rounded-sm p-4" data-testid={`admin-order-${order.id}`}>
                  <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                    <div>
                      <h3 className="font-semibold">Commande #{order.id.substring(0, 8)}</h3>
                      <p className="text-sm text-muted-foreground">{order.user_name} - {order.user_email}</p>
                      <p className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString('fr-FR')} • Paiement : {order.payment_status === 'paid' ? 'payé' : 'en attente'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-brand-orange">{order.total.toFixed(2)} €</p>
                      <select value={order.status} onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        className="mt-2 px-3 py-1 border border-border rounded-sm text-sm" data-testid={`order-status-${order.id}`}>
                        <option value="pending">En attente</option>
                        <option value="confirmed">Confirmée</option>
                        <option value="shipped">Expédiée</option>
                        <option value="delivered">Livrée</option>
                        <option value="cancelled">Annulée</option>
                      </select>
                    </div>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-0.5 mb-2">
                    {order.items?.map((item, idx) => (
                      <li key={idx}>{item.product_name} × {item.quantity}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">Livraison : {order.shipping_address}</p>
                  {order.shipping && (
                    <div className="mt-2 flex items-center gap-2 text-sm bg-surface rounded-sm px-3 py-2" data-testid={`order-tracking-${order.id}`}>
                      <Truck className="w-4 h-4 text-brand-orange flex-shrink-0" />
                      <span className="font-medium">{order.shipping.carrier}</span>
                      <span className="font-mono">{order.shipping.tracking_number}</span>
                      {order.shipping.tracking_url && (
                        <a href={order.shipping.tracking_url} target="_blank" rel="noreferrer" className="text-brand-orange hover:underline ml-auto">Suivre</a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {shippingOrder && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShippingOrder(null)}>
                <form onSubmit={submitShipping} onClick={(e) => e.stopPropagation()}
                  className="bg-white rounded-sm border border-border p-6 w-full max-w-md space-y-4" data-testid="shipping-modal">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg" style={{ fontFamily: 'IBM Plex Sans' }}>Expédier la commande #{shippingOrder.id.substring(0, 8)}</h3>
                    <button type="button" onClick={() => setShippingOrder(null)} data-testid="shipping-modal-close"><X className="w-5 h-5" /></button>
                  </div>
                  <p className="text-sm text-muted-foreground">Le client ({shippingOrder.user_email}) recevra un email avec le lien de suivi.</p>
                  <div>
                    <label className="block text-xs font-medium mb-1">Transporteur *</label>
                    <select value={shippingForm.carrier} onChange={(e) => setShippingForm({ ...shippingForm, carrier: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm" data-testid="shipping-carrier-select">
                      {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Numéro de suivi *</label>
                    <input type="text" required value={shippingForm.tracking_number}
                      onChange={(e) => setShippingForm({ ...shippingForm, tracking_number: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm font-mono" placeholder="ex: 6A12345678901" data-testid="shipping-tracking-input" />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={shippingLoading} className="btn-primary px-4 py-2 rounded-sm font-medium flex items-center gap-2 disabled:opacity-50" data-testid="shipping-submit-button">
                      <Truck className="w-4 h-4" /> {shippingLoading ? 'Envoi...' : 'Confirmer l\'expédition'}
                    </button>
                    <button type="button" onClick={() => setShippingOrder(null)} className="btn-secondary px-4 py-2 rounded-sm font-medium">Annuler</button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <AdminProducts products={products} categories={categories} refresh={fetchProducts} />
        )}

        {activeTab === 'promos' && <AdminPromoCodes />}
        {activeTab === 'pages' && <AdminPages />}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="bg-white rounded-sm border border-border p-6">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>Gestion des catégories</h2>
              <button onClick={() => setShowAddCategory(true)} className="btn-primary px-4 py-2 rounded-sm font-medium flex items-center gap-2" data-testid="add-category-button">
                <Plus className="w-4 h-4" /> Ajouter une catégorie
              </button>
            </div>

            {showAddCategory && (
              <form onSubmit={handleAddCategory} className="mb-6 p-4 bg-surface rounded-sm space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Nouvelle catégorie</h3>
                  <button type="button" onClick={() => setShowAddCategory(false)}><X className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Identifiant (ex: outils-electriques) *</label>
                    <input type="text" required pattern="[a-z0-9\-]+" value={newCategory.id}
                      onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm" placeholder="exemple-categorie" data-testid="category-id-input" />
                    <p className="text-xs text-muted-foreground mt-1">Minuscules, chiffres et tirets uniquement</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Nom affiché *</label>
                    <input type="text" required value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm" placeholder="Exemple Catégorie" data-testid="category-name-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Description</label>
                  <input type="text" value={newCategory.description} onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm" data-testid="category-description-input" />
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="btn-primary px-4 py-2 rounded-sm font-medium" data-testid="submit-category-button">Créer</button>
                  <button type="button" onClick={() => setShowAddCategory(false)} className="btn-secondary px-4 py-2 rounded-sm font-medium">Annuler</button>
                </div>
              </form>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {categories.map((cat) => {
                const productCount = products.filter(p => p.category === cat.id).length;
                return (
                  <div key={cat.id} className="border border-border rounded-sm p-4" data-testid={`admin-category-${cat.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold">{cat.name}</h3>
                        <p className="text-xs text-muted-foreground font-mono">{cat.id}</p>
                      </div>
                      <button onClick={() => deleteCategory(cat.id)}
                        className="p-1.5 border border-border rounded-sm hover:border-destructive hover:text-destructive"
                        data-testid={`delete-category-${cat.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    {cat.description && <p className="text-sm text-muted-foreground mb-2">{cat.description}</p>}
                    <p className="text-xs"><span className="font-medium">{productCount}</span> produit(s)</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Admin;
